import { UI_CONFIG, SEO_CONFIG, resolvePublicFileUrl } from '../config';
import { getRobotsContentForPath } from './adPolicy';

export type PageSeo = {
    title?: string;
    description?: string;
    keywords?: string;
    image?: string;
    imageAlt?: string;
};

type SeoImageMeta = { image: string; imageAlt: string };

type SeoImageOverride = SeoImageMeta & { prefix: string };

const SEO_IMAGE_PRESETS: Record<string, SeoImageMeta> = {
    default: {
        image: '/seo/bookai-share-default.png',
        imageAlt: 'Bookai - AI platform for modern teaching and learning.',
    },
    home: {
        image: '/seo/bookai-share-home.png',
        imageAlt: 'Bookai - AI cho giáo viên, học sinh và phụ huynh: tạo học liệu, quản lý lớp và học cá nhân hóa.',
    },
    teacher: {
        image: '/seo/bookai-share-teacher-tools.png',
        imageAlt: 'Bookai teacher tools - lesson plans, slides, quizzes, simulations, and learning activities.',
    },
    student: {
        image: '/seo/bookai-share-student-learning.png',
        imageAlt: 'Bookai learning journey - personalized lessons for students and parents.',
    },
    community: {
        image: '/seo/bookai-share-community-library.png',
        imageAlt: 'Bookai community library - shared educational resources and learning materials.',
    },
    design: {
        image: '/seo/bookai-share-design-studio.png',
        imageAlt: 'Bookai design studio - posters, flashcards, worksheets, and educational visuals.',
    },
    account: {
        image: '/seo/bookai-share-account-center.png',
        imageAlt: 'Bookai account center - login, settings, onboarding, and user support.',
    },
    billing: {
        image: '/seo/bookai-share-billing-premium.png',
        imageAlt: 'Bookai billing and premium plans - credits, transactions, and benefits.',
    },
    classroom: {
        image: '/seo/bookai-share-class-management.png',
        imageAlt: 'Bookai class management - students, assignments, and classroom progress tracking.',
    },
    interactive: {
        image: '/seo/bookai-share-interactive-video.png',
        imageAlt: 'Bookai interactive video - AI lessons with questions, notes, and learning checkpoints.',
    },
    brand: {
        image: '/seo/bookai-share-brand-trust.png',
        imageAlt: 'Bookai brand, trust, legal pages, and product guidance.',
    },
    career: {
        image: '/seo/bookai-share-career-journey.png',
        imageAlt: 'Bookai - Hành trình khám phá nghề nghiệp với AI cho học sinh tiểu học, THCS, THPT và sinh viên.',
    },
};

const SEO_IMAGE_GROUPS: Array<{ preset: keyof typeof SEO_IMAGE_PRESETS; prefixes: string[] }> = [
    {
        preset: 'teacher',
        prefixes: [
            '/cho-giao-vien',
            '/cong-cu-giao-vien',
            '/tao-giao-an',
            '/tao-de-kiem-tra',
            '/tao-tro-choi',
            '/tao-trinh-chieu',
            '/tao-bai-tap',
            '/tao-video',
            '/tao-sang-kien-kinh-nghiem',
            '/hoat-dong-sang-tao',
            '/mo-phong-khoa-hoc',
            '/thiet-ke-3d',
            '/kiem-tra-kien-thuc',
            '/phong-lab-ai',
            '/thu-thach-dao-duc-ai',
            '/kiem-chung-thong-tin-ai',
            '/slide-mau',
            '/soan-thao',
            '/giao-an',
            '/de-kiem-tra',
            '/tro-choi',
            '/trinh-chieu',
            '/kho-bai-tap',
            '/kho-hoat-dong',
            '/tai-lieu',
            '/video',
            '/skkn',
        ],
    },
    {
        preset: 'student',
        prefixes: [
            '/cho-hoc-sinh',
            '/cho-phu-huynh',
            '/bang-dieu-khien-phu-huynh',
            '/lo-trinh-hoc-tap',
            '/bang-hoc-cua-con',
            '/tham-gia-lop-hoc',
            '/tham-gia-lop',
            '/bai-hoc',
            '/luyen-phat-am',
            '/hoc-ve',
            '/ke-chuyen-lich-su',
            '/tro-ly-van-hoc',
            '/giai-thich-khoa-hoc',
        ],
    },
    {
        preset: 'community',
        prefixes: ['/thu-vien', '/thu-vien-cong-dong', '/chia-se'],
    },
    {
        preset: 'design',
        prefixes: ['/thiet-ke'],
    },
    {
        preset: 'interactive',
        prefixes: ['/video-tuong-tac'],
    },
    {
        preset: 'classroom',
        prefixes: ['/quan-ly-lop-hoc'],
    },
    {
        preset: 'billing',
        prefixes: ['/cua-hang-credit', '/lich-su-giao-dich', '/dac-quyen-premium', '/affiliate-dashboard'],
    },
    {
        preset: 'account',
        prefixes: ['/dang-nhap', '/chon-vai-tro', '/cai-dat'],
    },
    {
        preset: 'brand',
        prefixes: ['/huong-dan', '/gioi-thieu', '/dieu-khoan', '/chinh-sach-bao-mat', '/sap-ra-mat', '/tinh-nang'],
    },
    {
        preset: 'career',
        prefixes: ['/hanh-trinh-kham-pha', '/hanh-trinh-hoc-hang-ngay'],
    },
];

const SEO_IMAGE_ROUTE_OVERRIDES: SeoImageOverride[] = [
    {
        prefix: '/cong-cu-giao-vien',
        image: '/seo/cong-cu-giao-vien.png',
        imageAlt: 'Cong cu giao vien Bookai - day hoc va soan hoc lieu voi AI.',
    },
    {
        prefix: '/tao-giao-an',
        image: '/seo/tao-giao-an.png',
        imageAlt: 'Tao giao an Bookai - giao an AI cho giao vien.',
    },
    {
        prefix: '/tao-de-kiem-tra',
        image: '/seo/tao-de-kiem-tra.png',
        imageAlt: 'Tao de kiem tra Bookai - soan quiz va danh gia bang AI.',
    },
    {
        prefix: '/tao-tro-choi',
        image: '/seo/tao-tro-choi.png',
        imageAlt: 'Tao tro choi Bookai - game giao duc va on tap tuong tac.',
    },
    {
        prefix: '/tao-trinh-chieu',
        image: '/seo/tao-trinh-chieu.png',
        imageAlt: 'Tao trinh chieu Bookai - slide bai giang bang AI.',
    },
    {
        prefix: '/tao-bai-tap',
        image: '/seo/tao-bai-tap.png',
        imageAlt: 'Tao bai tap Bookai - giao bai va on tap voi AI.',
    },
    {
        prefix: '/tao-video',
        image: '/seo/tao-video.png',
        imageAlt: 'Tao video Bookai - video bai giang AI.',
    },
    {
        prefix: '/tao-sang-kien-kinh-nghiem',
        image: '/seo/tao-sang-kien-kinh-nghiem.png',
        imageAlt: 'Tao sang kien kinh nghiem Bookai - ho tro viet SKKN bang AI.',
    },
    {
        prefix: '/hoat-dong-sang-tao',
        image: '/seo/hoat-dong-sang-tao.png',
        imageAlt: 'Hoat dong sang tao Bookai - thiet ke hoat dong day hoc bang AI.',
    },
    {
        prefix: '/mo-phong-khoa-hoc',
        image: '/seo/mo-phong-khoa-hoc.png',
        imageAlt: 'Mo phong khoa hoc Bookai - hoc tap STEM tuong tac.',
    },
    {
        prefix: '/thiet-ke-3d',
        image: '/seo/thiet-ke-3d.png',
        imageAlt: 'Thiet ke 3D Bookai - mo hinh va hoc lieu 3D tuong tac.',
    },
    {
        prefix: '/kiem-tra-kien-thuc',
        image: '/seo/kiem-tra-kien-thuc.png',
        imageAlt: 'Kiem tra kien thuc Bookai - danh gia nhanh va oral quiz.',
    },
    {
        prefix: '/phong-lab-ai',
        image: '/seo/phong-thi-nghiem-ai.png',
        imageAlt: 'Phong Lab AI Bookai - 4 module thi nghiem AI cho hoc sinh.',
    },
    {
        prefix: '/thu-thach-dao-duc-ai',
        image: '/seo/thu-thach-dao-duc-ai.png',
        imageAlt: 'Thu thach dao duc AI Bookai - hoc va thuc hanh AI co trach nhiem.',
    },
    {
        prefix: '/kiem-chung-thong-tin-ai',
        image: '/seo/kiem-chung-thong-tin-ai.png',
        imageAlt: 'Kiem chung thong tin AI Bookai - ren luyen tu duy phan bien.',
    },
    {
        prefix: '/slide-mau',
        image: '/seo/slide-mau.png',
        imageAlt: 'Slide mau Bookai - thu vien template bai giang.',
    },
    {
        prefix: '/soan-thao',
        image: '/seo/soan-thao.png',
        imageAlt: 'Soan thao Bookai - chinh sua tai lieu day hoc.',
    },
    {
        prefix: '/giao-an',
        image: '/seo/giao-an.png',
        imageAlt: 'Giao an Bookai - xem va chinh sua giao an.',
    },
    {
        prefix: '/de-kiem-tra',
        image: '/seo/de-kiem-tra.png',
        imageAlt: 'De kiem tra Bookai - xem va chinh sua bai kiem tra.',
    },
    {
        prefix: '/tro-choi',
        image: '/seo/tro-choi.png',
        imageAlt: 'Tro choi Bookai - hoc tap va on tap bang game.',
    },
    {
        prefix: '/trinh-chieu',
        image: '/seo/trinh-chieu.png',
        imageAlt: 'Trinh chieu Bookai - bai giang slide va thuyet trinh.',
    },
    {
        prefix: '/kho-bai-tap',
        image: '/seo/kho-bai-tap.png',
        imageAlt: 'Kho bai tap Bookai - bai tap da luu va giao bai.',
    },
    {
        prefix: '/kho-hoat-dong',
        image: '/seo/kho-hoat-dong.png',
        imageAlt: 'Kho hoat dong Bookai - thu vien hoat dong day hoc.',
    },
    {
        prefix: '/tai-lieu',
        image: '/seo/tai-lieu.png',
        imageAlt: 'Tai lieu Bookai - tai nguyen hoc tap va giang day.',
    },
    {
        prefix: '/video',
        image: '/seo/video.png',
        imageAlt: 'Video Bookai - tai nguyen video bai giang.',
    },
    {
        prefix: '/skkn',
        image: '/seo/skkn.png',
        imageAlt: 'SKKN Bookai - sang kien kinh nghiem da luu.',
    },
    {
        prefix: '/hanh-trinh-kham-pha',
        image: '/seo/hanh-trinh-kham-pha.png',
        imageAlt: 'Hanh trinh kham pha nghe nghiep Bookai - AI dinh huong nghe cho hoc sinh tieu hoc, THCS, THPT, sinh vien.',
    },
    {
        prefix: '/hanh-trinh-hoc-hang-ngay',
        image: '/seo/hanh-trinh-hoc-hang-ngay.png',
        imageAlt: 'Hanh trinh hoc hang ngay Bookai - lo trinh hoc tap ca nhan hoa theo nghe mo uoc.',
    },
];

function normalizePathKey(path: string): string {
    let p = (path || '/').split('?')[0].split('#')[0];
    if (p !== '/' && p.endsWith('/')) p = p.replace(/\/+$/, '');
    return p || '/';
}

function resolveAbsoluteUrl(value: string): string {
    if (!value) return '';
    if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:')) {
        return value;
    }

    const publicUrl = resolvePublicFileUrl(value);
    if (/^(https?:)?\/\//i.test(publicUrl)) {
        return publicUrl;
    }

    const normalized = publicUrl || (value.startsWith('/') ? value : `/${value}`);
    if (typeof window === 'undefined' || !window.location?.origin) {
        return normalized;
    }

    return new URL(normalized, window.location.origin).toString();
}

function detectImageMime(url: string): string {
    const clean = url.split('?')[0].toLowerCase();
    if (clean.endsWith('.png')) return 'image/png';
    if (clean.endsWith('.webp')) return 'image/webp';
    if (clean.endsWith('.svg')) return 'image/svg+xml';
    return 'image/jpeg';
}

function resolveImageMeta(pathKey: string, pageMeta?: PageSeo): SeoImageMeta {
    if (pageMeta?.image) {
        return {
            image: resolveAbsoluteUrl(pageMeta.image),
            imageAlt: pageMeta.imageAlt || SEO_IMAGE_PRESETS.default.imageAlt,
        };
    }

    const routeOverride = SEO_IMAGE_ROUTE_OVERRIDES
        .slice()
        .sort((a, b) => b.prefix.length - a.prefix.length)
        .find((item) => pathKey === item.prefix || pathKey.startsWith(`${item.prefix}/`));
    if (routeOverride) {
        return {
            image: resolveAbsoluteUrl(routeOverride.image),
            imageAlt: routeOverride.imageAlt,
        };
    }

    if (pathKey === '/') {
        return {
            image: resolveAbsoluteUrl(SEO_IMAGE_PRESETS.home.image),
            imageAlt: SEO_IMAGE_PRESETS.home.imageAlt,
        };
    }

    const matchedGroup = SEO_IMAGE_GROUPS.find((group) =>
        group.prefixes.some((prefix) => pathKey === prefix || pathKey.startsWith(`${prefix}/`))
    );
    const preset = matchedGroup ? SEO_IMAGE_PRESETS[matchedGroup.preset] : SEO_IMAGE_PRESETS.default;

    return {
        image: resolveAbsoluteUrl(preset.image),
        imageAlt: preset.imageAlt,
    };
}

/**
 * Khớp route dài nhất trong SEO_CONFIG.PAGES (hỗ trợ /tao-giao-an, /mo-phong-khoa-hoc/tao, …).
 */
function findPageMeta(pathKey: string): PageSeo | undefined {
    const pages = SEO_CONFIG?.PAGES as Record<string, PageSeo> | undefined;
    if (!pages) return undefined;
    const normalized = normalizePathKey(pathKey || '/');
    if (pages[normalized]) return pages[normalized];
    const keys = Object.keys(pages)
        .filter((k) => k !== '/')
        .sort((a, b) => b.length - a.length);
    for (const k of keys) {
        if (normalized === k || normalized.startsWith(`${k}/`)) {
            return pages[k];
        }
    }
    /** Không khớp prefix nào: trả undefined để dùng DEFAULT_* thay vì gán nhầm meta trang chủ. */
    return undefined;
}

/** Meta tĩnh (sau merge admin) cho một path — dùng khi build title động theo tên tài nguyên */
export function getSeoMetaForPath(path: string): PageSeo | undefined {
    return findPageMeta(normalizePathKey(path || '/'));
}

/**
 * Title/description/keywords cho trang có tên riêng (giáo án, mô phỏng, đề kiểm tra, …).
 * Kết hợp prefix từ SEO_CONFIG.PAGES với tên hiển thị.
 */
export function buildNamedResourceSeo(
    path: string,
    name: string,
    options?: { resourceKind?: string; extraDescription?: string; extraKeywords?: string }
): PageSeo {
    const base = findPageMeta(path) || {};
    const imageMeta = resolveImageMeta(normalizePathKey(path), base);
    const kind = options?.resourceKind || 'Học liệu';
    const trimmed = (name || '').trim();
    const title = trimmed ? `${trimmed} — ${kind}` : base.title || '';
    const desc =
        (options?.extraDescription && String(options.extraDescription).trim().slice(0, 160)) ||
        (trimmed
            ? `${kind} «${trimmed.length > 80 ? `${trimmed.slice(0, 80)}…` : trimmed}» trên Bookai — xem, chỉnh sửa và chia sẻ.`
            : base.description || '');
    const kwParts = [trimmed, kind, options?.extraKeywords, base.keywords].filter(Boolean) as string[];
    const keywords = kwParts.join(', ').slice(0, 400);
    return {
        title,
        description: desc,
        keywords,
        image: base.image || imageMeta.image,
        imageAlt: base.imageAlt || imageMeta.imageAlt,
    };
}

/**
 * Tra ve JSON-LD structured data theo path - giup Google hieu la WebApplication
 * va lam giau search snippet (rich result).
 *
 * Hien tai chi inject cho cac trang feature lon - tranh spam Google bot.
 */
function buildStructuredData(pathKey: string, title: string, description: string, image: string): Record<string, any> | null {
    const appName = UI_CONFIG?.APP_NAME || 'Bookai';
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://bookai.vn';

    // Trang Hanh trinh kham pha nghe nghiep - WebApplication + EducationalOccupationalProgram
    if (pathKey === '/hanh-trinh-kham-pha' || pathKey.startsWith('/hanh-trinh-kham-pha/')) {
        return {
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'WebApplication',
                    name: 'Hành trình khám phá nghề nghiệp',
                    description,
                    url: `${baseUrl}/hanh-trinh-kham-pha`,
                    applicationCategory: 'EducationApplication',
                    operatingSystem: 'Web',
                    image,
                    inLanguage: 'vi-VN',
                    audience: [
                        { '@type': 'EducationalAudience', educationalRole: 'student' },
                        { '@type': 'EducationalAudience', educationalRole: 'parent' },
                    ],
                    offers: {
                        '@type': 'Offer',
                        price: '0',
                        priceCurrency: 'VND',
                        description: 'Miễn phí 1 hồ sơ + 3 nghề lưu. Nâng cấp gói để mở khoá nhiều con và nhiều nghề hơn.',
                    },
                    publisher: { '@type': 'Organization', name: appName, url: baseUrl },
                },
                {
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        { '@type': 'ListItem', position: 1, name: 'Trang chủ', item: baseUrl },
                        { '@type': 'ListItem', position: 2, name: title, item: `${baseUrl}/hanh-trinh-kham-pha` },
                    ],
                },
            ],
        };
    }

    if (pathKey === '/hanh-trinh-hoc-hang-ngay' || pathKey.startsWith('/hanh-trinh-hoc-hang-ngay/')) {
        return {
            '@context': 'https://schema.org',
            '@type': 'Course',
            name: title,
            description,
            provider: { '@type': 'Organization', name: appName, url: baseUrl },
            inLanguage: 'vi-VN',
            educationalLevel: 'K-12 + University',
            image,
            offers: {
                '@type': 'Offer',
                category: 'Subscription',
                priceCurrency: 'VND',
                description: 'Lớp học mỗi ngày 15 phút theo nghề mơ ước - mở theo gói credit của Bookai.',
            },
        };
    }

    return null;
}

/**
 * Cập nhật document.title và meta tags (description, keywords) theo path hiện tại.
 * Gọi khi route thay đổi.
 */
export function updatePageSEO(path: string, dynamicMeta?: PageSeo): void {
    const pathKey = normalizePathKey(path);
    const pageMeta = dynamicMeta || findPageMeta(pathKey);
    const appName = UI_CONFIG?.APP_NAME || 'Bookai';

    const title = pageMeta?.title || SEO_CONFIG?.DEFAULT_TITLE || appName;
    const description = pageMeta?.description || SEO_CONFIG?.DEFAULT_DESCRIPTION || '';
    const keywords = pageMeta?.keywords || SEO_CONFIG?.DEFAULT_KEYWORDS || '';
    const imageMeta = resolveImageMeta(pathKey, pageMeta);
    const imageMime = detectImageMime(imageMeta.image);

    if (typeof document !== 'undefined') {
        document.title = title.includes(appName) ? title : `${title} | ${appName}`;

        const setMeta = (name: string, content: string, isProperty = false) => {
            if (!content) return;
            const attr = isProperty ? 'property' : 'name';
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };

        setMeta('description', description);
        setMeta('keywords', keywords);
        setMeta('robots', getRobotsContentForPath(pathKey));
        setMeta('og:title', document.title, true);
        setMeta('og:description', description, true);
        setMeta('og:url', window.location.href.split('?')[0].split('#')[0], true);
        setMeta('og:type', 'website', true);
        setMeta('og:locale', 'vi_VN', true);
        setMeta('og:site_name', appName, true);
        setMeta('og:image', imageMeta.image, true);
        setMeta('og:image:alt', imageMeta.imageAlt, true);
        setMeta('og:image:type', imageMime, true);
        setMeta('og:image:width', '1200', true);
        setMeta('og:image:height', '630', true);
        setMeta('twitter:card', 'summary_large_image');
        setMeta('twitter:title', document.title);
        setMeta('twitter:description', description);
        setMeta('twitter:image', imageMeta.image);
        setMeta('twitter:image:alt', imageMeta.imageAlt);

        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', window.location.href.split('?')[0].split('#')[0]);

        let imageSrc = document.querySelector('link[rel="image_src"]');
        if (!imageSrc) {
            imageSrc = document.createElement('link');
            imageSrc.setAttribute('rel', 'image_src');
            document.head.appendChild(imageSrc);
        }
        imageSrc.setAttribute('href', imageMeta.image);

        // Inject JSON-LD structured data cho cac trang feature lon (rich snippet)
        const jsonLd = buildStructuredData(pathKey, title, description, imageMeta.image);
        const STRUCTURED_DATA_ID = 'bookai-structured-data';
        let scriptEl = document.getElementById(STRUCTURED_DATA_ID) as HTMLScriptElement | null;
        if (jsonLd) {
            if (!scriptEl) {
                scriptEl = document.createElement('script');
                scriptEl.id = STRUCTURED_DATA_ID;
                scriptEl.setAttribute('type', 'application/ld+json');
                document.head.appendChild(scriptEl);
            }
            scriptEl.textContent = JSON.stringify(jsonLd);
        } else if (scriptEl) {
            scriptEl.remove();
        }
    }
}
